"use client";

// Packages
import React, { useState, useEffect } from "react";
import { useSession } from 'next-auth/react';
import { useForm } from "react-hook-form";

// Component imports
import StylizedButton from "@/components/stylizedButton";
import { Rating as ReactRating, StickerStar } from "@smastrom/react-rating";

// Functions
import { submitProductReview } from "@/lib/functions/submitProductReview";

// Styles
import "@smastrom/react-rating/style.css";
import styles from "./submitReview.module.scss";

interface SubmitReviewProps {
  onClose: () => void;
  productCode: string;
  onSuccess: () => void;
}

interface ReviewForm {
  rating: number;
  comment: string;
  productCode: string;
}

export const SubmitReview: React.FC<SubmitReviewProps> = ({ onClose, productCode, onSuccess }) => {
  const {
    register,
    handleSubmit,
    setValue,
    // watch,
    formState: { isValid, errors },
  } = useForm<ReviewForm>({
    mode: "onChange",
    defaultValues: {
      rating: 0,
      comment: '',
    },
  });

  const [rating, setRating] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { status, data: session } = useSession();
  const userId = session?.user?.customerId;

  const ratingStyles = {
    itemShapes: StickerStar,
    activeFillColor: "#dc181f",
    inactiveFillColor: "#ffffff",
    itemStrokeWidth: 1,
  };

  useEffect(() => {
    setValue("productCode", productCode);
  }, [productCode, setValue]);

  // Store the rating value
  const handleRating = (value: number) => {
    setRating(value);
    setValue("rating", value, { shouldValidate: true });
  };

  // Submit the form data to the API
  const onSubmit = async (data: ReviewForm) => {
    if (status === 'loading' || !userId) return;

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const resp = await submitProductReview(data, session?.token);

      if (resp.status !== 201) {
        setErrorMessage("Failed to submit the review. Please try again.");
        return;
      }

      onClose();
      onSuccess();
    } catch {
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }

    setSubmitting(false);
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.content}>

          <h1>Leave a Review</h1>

          <form onSubmit={handleSubmit(onSubmit)} className={styles.reviewForm}>
            <div className={styles.formRow}>
              <h3>Your Rating <span>*</span></h3>
              <ReactRating
                className={styles.ratingsWrapper}
                value={rating}
                onChange={handleRating}
                itemStyles={ratingStyles}
              />
              <input
                type="hidden"
                {...register("rating", {
                  validate: (value) => value >= 1 || "Rating is required",
                })}
              />
              {errors.rating && <p className={styles.error}>{errors.rating.message}</p>}
            </div>

            <div className={styles.formRow}>
              <label htmlFor="review_text">
                <h3>Enter Your Review Text Below <span>*</span></h3>
              </label>
              <textarea
                id="review_text"
                {...register("comment", {
                  required: "Review text is required",
                  minLength: {
                    value: 2,
                    message: "Review must be at least 2 characters long",
                  },
                  maxLength: {
                    value: 500,
                    message: "Review must be less than 500 characters",
                  },
                  validate: (value) => {
                    const trimmedValue = value.trim();
                    if (trimmedValue.length < 2) return "Review must be at least 2 non-space characters";

                    const hasHTMLTags = /<[^>]*>/g.test(trimmedValue);
                    const hasSQLInjection = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|SCRIPT|iframe|onerror|onload|javascript):?\b)/gi.test(trimmedValue);

                    if (hasHTMLTags) return "HTML is not allowed";
                    if (hasSQLInjection) return "Invalid text found, please edit your comment";

                    return true;
                  },
                })}
              />
              {errors.comment && <p className={styles.error}>{errors.comment.message}</p>}
            </div>

            {/* Hidden Fields */}
            <input type="hidden" {...register("productCode")} />

            <div className={styles.submitButton}>
              <StylizedButton type="submit" variant="primary" size="small" disabled={submitting || !isValid || status === 'loading'}>
                {submitting ? "Submitting..." : "Submit Review"}
              </StylizedButton>
            </div>
          </form>

          <div className={styles.formError}>
            {errorMessage && <p className={styles.error}>{errorMessage}</p>}
          </div>

        </div>
      </div>
    </>
  );
};
